import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import GlobalTimer from './GlobalTimer';
import CommandPalette from './GlobalSearch';
import UpdatePrompt from './UpdatePrompt';
import PushBanner from './PushBanner';
import WhatsNew from './WhatsNew';
import MobileNav from './MobileNav';
import { notifyExtensionsLogout } from './ExtensionInstall';
import ChatNotificationDock from './ChatNotificationDock';
import ChatBubble from './ChatBubble';
import { useNotificationToasts } from '../hooks/useNotificationToasts';
import { useReminderToasts } from '../hooks/useReminderToasts';
import BugReportHost from './BugReportButton';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import ClockInNudge from './attendance/ClockInNudge';
import ExtensionInstall from './ExtensionInstall';
import SkipLink from './ui/SkipLink';
import { QuickCreateHost } from './QuickCreate';
import { useActiveNavPath, useNavGroups } from './navigation';
import { useBrand } from '../providers/BrandProvider';
import CoverageHost from './customerCoverage/CoverageHost';

const SIDEBAR_KEY = 'ote_sidebar_collapsed';

/**
 * AppShell (PRD §4.1, M3): SkipLink → Sidebar (start side) → column { TopBar,
 * impersonation banner, main#main } → floating layers. One instance each of
 * the command palette, QuickCreate and bug-report hosts (attendance lives in
 * the TopBar, `attendance/AttendanceControl`).
 *
 * The shell regions carry `data-overlay-inert`: while a `ui/Dialog` is open
 * they become `inert`, so only the dialog is reachable (toasts and live
 * regions stay outside and keep announcing).
 */
export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  // FIX-NOTIF N-5 (D-145 / D-146 Q-100) — the employee's pop-up for what asks a reaction: a ticket that reached
  // them, a reply, an approval request, being added to a conversation or a task. One policy, one hook.
  useNotificationToasts({ enabled: user?.role === 'admin' || user?.role === 'agent', audience: 'staff' });
  // W-01 - the pop-up of a "התראה חדשה" reminder (same poll as the bell).
  useReminderToasts(user?.role === 'admin' || user?.role === 'agent');
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      try { localStorage.setItem(SIDEBAR_KEY, v ? '0' : '1'); } catch { /* storage unavailable */ }
      return !v;
    });
  };

  const handleLogout = () => {
    queryClient.clear();
    notifyExtensionsLogout();
    clearAuth();
    navigate('/login');
  };

  // T-12: after a route change, move focus to the main landmark so keyboard and
  // screen-reader users start at the new page, not on the link they used.
  // Never out of an open dialog. C-23 (clarity, B-03, D-18): on the FIRST load
  // too, when nothing holds focus yet, so the first Tab starts at the content
  // and not somewhere in the sidebar (23–26 stops away).
  const mainRef = useRef<HTMLElement>(null);
  const prevPath = useRef<string | null>(null);
  useEffect(() => {
    const firstLoad = prevPath.current === null;
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;
    if (document.querySelector('[aria-modal="true"]')) return;
    if (firstLoad && document.activeElement && document.activeElement !== document.body) return;
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  // B-15 (clarity): the browser tab names the screen: "<active menu item> · <brand>".
  // Detail pages (`/tickets/:id`…) resolve to their list item (most specific match).
  const brand = useBrand();
  const navGroups = useNavGroups();
  const activePath = useActiveNavPath(navGroups, location.pathname);
  useEffect(() => {
    const label = activePath ? navGroups.flatMap((g) => g.items).find((i) => i.to === activePath)?.label : undefined;
    document.title = label ? `${label} · ${brand.browserTitle}` : brand.browserTitle;
  }, [activePath, navGroups, brand.browserTitle]);
  useEffect(() => () => { document.title = brand.browserTitle; }, [brand.browserTitle]);

  return (
    <div className="app-shell min-h-screen bg-canvas flex">
      <SkipLink />
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      <div
        data-overlay-inert=""
        className={`flex-1 min-w-0 flex flex-col min-h-screen ${sidebarCollapsed ? 'md:ms-rail' : 'md:ms-sidebar'}`}
      >
        <TopBar onLogout={handleLogout} />
        {/* "עוד לא החתמת כניסה היום": once a day, in the flow under the bar (never over content). */}
        <ClockInNudge />
        {/* C-30 (clarity, N-04): the one-time "התקנת התוסף" notice, in the flow too; "לא עכשיו" is remembered per user. */}
        <ExtensionInstall />
        {/* D-322: approve browser notices - a bar from the first visit, "לא עכשיו" = until tomorrow. */}
        <PushBanner />

        {localStorage.getItem('ote_impersonating') === 'true' && (
          <div className="bg-warning-50 text-warning-900 border-b border-warning-200 px-4 py-2 text-center text-sm flex items-center justify-center gap-3 flex-wrap" role="status">
            <span>{t('admin.impersonatingAs')} <strong>{user?.name}</strong> (<span dir="ltr">{user?.email}</span>)</span>
            <button
              type="button"
              onClick={() => {
                const originalToken = localStorage.getItem('ote_original_token');
                if (originalToken) {
                  localStorage.setItem('auth_token', originalToken);
                  localStorage.removeItem('ote_original_token');
                }
                localStorage.removeItem('ote_impersonating');
                queryClient.clear();
                window.location.href = '/';
              }}
              className="btn btn-secondary btn-sm"
            >
              {t('admin.stopImpersonation')}
            </button>
          </div>
        )}

        <main
          id="main"
          ref={mainRef}
          tabIndex={-1}
          className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 pt-6 pb-fab focus:outline-none"
        >
          <Outlet />
        </main>
      </div>

      <GlobalTimer />
      <ChatNotificationDock />
      <ChatBubble />
      <MobileNav onLogout={handleLogout} />
      <UpdatePrompt />
      <QuickCreateHost />
      <CommandPalette />
      <BugReportHost />
      <WhatsNew />
      {/* FIX-D300-S2 — a 409 CUSTOMER_NO_COVERAGE anywhere opens "שליחת הודעה למנהל" (only with the switch on). */}
      <CoverageHost />
    </div>
  );
}
